import { useMemo } from "react";
import type { TreeSelectNode } from "../../../shared/TreeSelect";

// Subset of TargetOption fields the hook needs. Keeping it as a separate type lets this hook
// stay decoupled from the TemplateTriggersTab internals.
// phaseId/stepId are only used to build node ids, so they accept either template ids (number)
// or grant-instance ids (Guid string) — the notes tree reuses this hook with string ids.
export type TriggerTargetItem = {
    key: string;
    label: string;
    moduleName: string;
    phaseName: string;
    stepName?: string;
    isStep: boolean;
    phaseId: number | string;
    stepId?: number | string;
};

type Result = {
    nodes: TreeSelectNode[];
    selectedLabel: string;
};

type Options<T> = {
    // When provided, an item that returns false stays visible in the tree but is rendered
    // disabled (greyed out, not clickable). Use this to keep hierarchy context intact while
    // ruling out specific items for the current field/context — e.g. Step-workflow phases
    // when the field is "status".
    isSelectable?: (item: T) => boolean;
    // Hint shown next to disabled nodes to explain why they can't be picked.
    disabledReason?: string;
    // Returns a short pill label to render next to a phase node (e.g. workflow type).
    // Steps don't get a badge in this hook.
    getPhaseBadge?: (item: T) => string | undefined;
};

// Adapts the flat trigger-target list into the tree shape consumed by <TreeSelect>.
// Module nodes are non-selectable group rows. A phase node carries its targetKey only when
// the phase itself is in `targets` (e.g. excluded for the editing phase). Step nodes are
// selectable leaves. The optional `isSelectable` predicate marks individual items as
// disabled without removing them from the tree.
//
// Generic in T so callers can supply a predicate keyed to a richer item type
// (e.g. with workflowType or other domain fields beyond TriggerTargetItem).
export function useTriggerTargetTree<T extends TriggerTargetItem>(
    targets: T[],
    value: string,
    options: Options<T> = {}
): Result {
    const { isSelectable, disabledReason, getPhaseBadge } = options;

    const nodes = useMemo<TreeSelectNode[]>(() => {
        const result: TreeSelectNode[] = [];
        const seenModule = new Set<string>();
        const seenPhase = new Set<string>();

        for (const t of targets) {
            const moduleNodeId = `m:${t.moduleName}`;
            if (!seenModule.has(moduleNodeId)) {
                seenModule.add(moduleNodeId);
                result.push({
                    id: moduleNodeId,
                    parentId: null,
                    text: t.moduleName,
                    expanded: true
                });
            }

            const phaseNodeId = `p:${t.phaseId}`;
            if (!seenPhase.has(phaseNodeId)) {
                seenPhase.add(phaseNodeId);
                const phaseTarget = targets.find(o => !o.isStep && o.phaseId === t.phaseId);
                const phaseDisabled = phaseTarget && isSelectable
                    ? !isSelectable(phaseTarget)
                    : false;
                const badgeSource = phaseTarget ?? t;
                result.push({
                    id: phaseNodeId,
                    parentId: moduleNodeId,
                    text: t.phaseName,
                    targetKey: phaseTarget?.key,
                    disabled: phaseDisabled,
                    disabledReason: phaseDisabled ? disabledReason : undefined,
                    badge: getPhaseBadge ? getPhaseBadge(badgeSource) : undefined,
                    expanded: true
                });
            }

            if (t.isStep) {
                const stepDisabled = isSelectable ? !isSelectable(t) : false;
                result.push({
                    id: `s:${t.stepId}`,
                    parentId: phaseNodeId,
                    text: t.stepName ?? "",
                    targetKey: t.key,
                    disabled: stepDisabled,
                    disabledReason: stepDisabled ? disabledReason : undefined
                });
            }
        }
        return result;
    }, [targets, isSelectable, disabledReason, getPhaseBadge]);

    const selectedLabel = useMemo(
        () => targets.find(t => t.key === value)?.label ?? "",
        [targets, value]
    );

    return { nodes, selectedLabel };
}
